import {
  convertDateToYYYYMMDDFormat,
  STATUSPAGE,
  SUBMIT_BUTTON,
} from '@repo/ui';
import { HandleActivitiesAnalyticalPerGroupUseCaseInterface } from 'domain/usecases';
import { useFormik } from 'formik';
import { notifyError } from 'presentation/app/components/notify';
import { useLegends } from 'presentation/app/hooks/useLegends';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearGroupsListData,
  loadGroupsListData,
} from 'presentation/config/store/reports/activities-analytical-per-group-slice';
import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

export const useActivitiesAnalyticalPerGroup = () => {
  // Hooks
  const { reportService } = useService();
  const dispatch = useDispatch();
  const { getLegends } = useLegends();

  // States
  const selectedSubmitBtnRef = useRef<SUBMIT_BUTTON | null>(null);
  const [loading, setLoading] = useState({
    loadingButtons: STATUSPAGE.DONE,
    loadingTable: STATUSPAGE.DONE,
  });
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const { groupsListData, filters } = useAppSelector(
    (state) => state.activitiesAnalyticalPerGroup,
  );

  const todaysDate = new Date();
  const firstDayOfMonth = new Date(
    todaysDate.getFullYear(),
    todaysDate.getMonth(),
    1,
  );

  // Methods
  const toggleLoading = (key: string, status: STATUSPAGE) => {
    setLoading((prevState) => ({
      ...prevState,
      [key]: status,
    }));
  };

  const requestHandleActivitiesAnalyticalPerGroupPDFOrExcel = useCallback(
    async (
      params: HandleActivitiesAnalyticalPerGroupUseCaseInterface.Params,
    ) => {
      toggleLoading('loadingButtons', STATUSPAGE.LOADING);
      await reportService
        .handleActivitiesAnalyticalPerGroup(params)
        .then((res: any) => {
          let blob = new Blob([res], {
            type: `application/${params?.responseType === '0' ? 'pdf' : 'vnd.openxmlformats-officedocument.spreadsheetml.sheet'}`,
          });
          let link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          window.open(link.href, '_blank');
        })
        .catch((err) => {
          toggleLoading('loadingButtons', STATUSPAGE.ERROR);
          notifyError(err.message);
        })
        .finally(() => {
          toggleLoading('loadingButtons', STATUSPAGE.DONE);
        });
    },

    [],
  );

  const requestHandleActivitiesAnalyticalPerGroupJSON = useCallback(
    async (
      params: HandleActivitiesAnalyticalPerGroupUseCaseInterface.Params,
    ) => {
      toggleLoading('loadingButtons', STATUSPAGE.LOADING);
      toggleLoading('loadingTable', STATUSPAGE.LOADING);
      await reportService
        .handleActivitiesAnalyticalPerGroup(params)
        .then((res) => {
          dispatch(loadGroupsListData(res));
        })
        .catch((err) => {
          toggleLoading('loadingButtons', STATUSPAGE.ERROR);
          toggleLoading('loadingTable', STATUSPAGE.ERROR);
          notifyError(err.message);
          dispatch(clearGroupsListData());
        })
        .finally(() => {
          toggleLoading('loadingButtons', STATUSPAGE.DONE);
          toggleLoading('loadingTable', STATUSPAGE.DONE);
          setHasFetchedData(true);
        });
    },
    [],
  );

  // Form Validation
  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      entity: filters?.entity || null,
      course: filters?.course || null,
      unit: filters?.unit || null,
      groups: filters?.groups || [],
      semester: filters?.semester || null,
      activityStatus: filters?.activityStatus || [],
      validation: filters?.validation || null,
      starts: filters?.starts || convertDateToYYYYMMDDFormat(firstDayOfMonth),
      ends: filters?.ends || convertDateToYYYYMMDDFormat(todaysDate),
    },
    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      unit: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      groups: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable(),
      ),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      semester: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      activityStatus: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable(),
      ),
      validation: Yup.string().nullable(),
      starts: Yup.date().nullable().required('Data inicial é obrigatório'),
      ends: Yup.date().nullable().required('Data final é obrigatório'),
    }),

    onSubmit: (values) => {
      const valuesToSubmit = {
        entity: values?.entity?.value,
        groups: values?.groups?.map((group) => group.value),
        course: values?.course?.value,
        curricularUnit: values?.unit?.value,
        semester: values?.semester?.value,
        validation: values?.validation,
        activityStatus: values?.activityStatus?.map(
          (activityStatus) => activityStatus?.value,
        ),
        starts: values?.starts,
        ends: values?.ends,
      };

      switch (selectedSubmitBtnRef.current) {
        case SUBMIT_BUTTON.PDF:
          requestHandleActivitiesAnalyticalPerGroupPDFOrExcel({
            ...valuesToSubmit,
            responseType: '0',
          });
          break;
        case SUBMIT_BUTTON.EXCEL:
          requestHandleActivitiesAnalyticalPerGroupPDFOrExcel({
            ...valuesToSubmit,
            responseType: '1',
          });
          break;
        case SUBMIT_BUTTON.JSON:
          requestHandleActivitiesAnalyticalPerGroupJSON({
            ...valuesToSubmit,
            responseType: '2',
          });
          break;
        default:
          break;
      }
    },
  });
  return {
    validation,
    loading,
    groupsListData,
    hasFetchedData,
    selectedSubmitBtnRef,
    getLegends,
  };
};
